// src/admin/LiturgyBoard.js - Giao diện Ban Phụng Vụ với Thống Kê Đi Lễ & Sidebar động
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, Outlet, NavLink, useLocation } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import TopNav from '../components/TopNav';
import { firestore } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import './LiturgyBoard.css';

const cleanVietnameseId = (str) => {
  if (!str) return '';
  let slug = str.toString().toLowerCase();
  slug = slug.replace(/đ/g, 'd');
  slug = slug.replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a');
  slug = slug.replace(/[éèẻẽẹêếềểễệ]/g, 'e');
  slug = slug.replace(/[íìỉĩị]/g, 'i');
  slug = slug.replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o');
  slug = slug.replace(/[úùủũụưứừửữự]/g, 'u');
  slug = slug.replace(/[ýỳỷỹỵ]/g, 'y');
  slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  slug = slug.replace(/[^a-z0-9]/g, '-');
  slug = slug.replace(/-+/g, '-');
  slug = slug.replace(/^-+|-+$/g, '');
  return slug;
};

const LiturgyBoard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { studentsData, isDataLoaded, loadAndFilterData } = useContext(StudentContext);

  const [selectedClass, setSelectedClass] = useState('all');
  const [openBlocks, setOpenBlocks] = useState({});

  const [dynamicBlocks, setDynamicBlocks] = useState({});
  const [currentYearInfo, setCurrentYearInfo] = useState({ id: '', name: '' });
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const isLoggedIn = localStorage.getItem('isLoggedIn');

  const hasPermission = () => {
    const userRole = localStorage.getItem('userRole') || 'catechist';
    if (userRole === 'admin') return true;

    const rawDept = localStorage.getItem('departmentLeader') || '';
    if (!rawDept) return false;

    let depts = rawDept;
    try {
      depts = JSON.parse(rawDept);
    } catch (e) {
      // Không phải JSON
    }

    if (Array.isArray(depts)) {
      return depts.includes('Ban Phụng Vụ');
    }
    if (typeof depts === 'string') {
      return depts.split(',').map((s) => s.trim()).includes('Ban Phụng Vụ');
    }
    return false;
  };

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login', { replace: true });
    } else if (!hasPermission()) {
      navigate('/main', { replace: true });
    }
  }, [navigate, isLoggedIn]);

  useEffect(() => {
    if (!isDataLoaded) {
      loadAndFilterData();
    }
  }, [isDataLoaded, loadAndFilterData]);

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname, selectedClass]);

  useEffect(() => {
    const fetchCurrentYearStructure = async () => {
      try {
        setSidebarLoading(true);
        const yearQuery = query(collection(firestore, 'academic_years'), where('isCurrent', '==', true));
        let yearSnap = await getDocs(yearQuery);
        let currentYearDoc = yearSnap.docs[0];

        if (!currentYearDoc) {
          const allYearsSnap = await getDocs(query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc')));
          if (!allYearsSnap.empty) {
            currentYearDoc = allYearsSnap.docs[0];
          }
        }

        if (!currentYearDoc) {
          setSidebarLoading(false);
          return;
        }

        const yearId = currentYearDoc.id;
        const yearData = currentYearDoc.data();
        setCurrentYearInfo({ id: yearId, name: yearData.name || yearId });

        const gradesSnap = await getDocs(collection(firestore, `academic_years/${yearId}/grades`));
        const gradesList = gradesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        const gradeOrderMap = {};
        gradesList.forEach(g => {
          gradeOrderMap[g.id] = g.sortOrder || 0;
        });

        const classesSnap = await getDocs(collection(firestore, `academic_years/${yearId}/classes`));
        const classesList = classesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        classesList.sort((a, b) => {
          const orderA = gradeOrderMap[a.gradeId] ?? 999;
          const orderB = gradeOrderMap[b.gradeId] ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        });

        const blocksObj = {};
        classesList.forEach(c => {
          const groupName = c.group || 'Khác';
          const groupKey = cleanVietnameseId(groupName) || 'khac';

          if (!blocksObj[groupKey]) {
            blocksObj[groupKey] = {
              groupName: groupName,
              classes: []
            };
          }
          blocksObj[groupKey].classes.push({
            id: c.id,
            name: c.name
          });
        });

        setDynamicBlocks(blocksObj);

        const firstGroupKey = Object.keys(blocksObj)[0];
        if (firstGroupKey) {
          setOpenBlocks({ [firstGroupKey]: true });
        }

      } catch (err) {
        console.error("Lỗi khi tải cấu trúc sidebar Ban Phụng Vụ từ Firestore:", err);
      } finally {
        setSidebarLoading(false);
      }
    };

    fetchCurrentYearStructure();
  }, []);

  const toggleBlock = (blockKey) => {
    setOpenBlocks((prev) => ({ ...prev, [blockKey]: !prev[blockKey] }));
  };

  const handleSelectClass = (classId) => {
    setSelectedClass(classId);
    setIsMobileSidebarOpen(false);
  };

  const selectedClassNameDisplay = useMemo(() => {
    if (selectedClass === 'all') return 'Tất cả các lớp';
    for (const key in dynamicBlocks) {
      const found = dynamicBlocks[key].classes.find(c => c.id === selectedClass);
      if (found) return found.name;
    }
    return selectedClass;
  }, [selectedClass, dynamicBlocks]);

  const allStudentsForOutlet = useMemo(() => {
    const students = studentsData || {};
    if (selectedClass === 'all') {
      return Object.keys(students).flatMap((classId) => students[classId] || []);
    } else {
      return students[selectedClass] || [];
    }
  }, [studentsData, selectedClass]);

  const renderNavTabs = () => {
    const navLinkClasses = ({ isActive }) => `lb-nav-tab ${isActive ? 'active' : ''}`;

    return (
      <nav className="lb-nav-tabs">
        <ul className="lb-nav-list">
          <li>
            <NavLink to="early-attendance" className={navLinkClasses}>
              📊 Thống kê Đi Lễ
            </NavLink>
          </li>
        </ul>
      </nav>
    );
  };

  const renderSidebar = () => {
    return (
      <>
        {isMobileSidebarOpen && (
          <div 
            className="lb-sidebar-backdrop" 
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        <aside className={`lb-sidebar ${isMobileSidebarOpen ? 'open' : ''}`}>
          <div className="lb-sidebar-mobile-header">
            <h3>📂 Chọn Lớp Học</h3>
            <button 
              type="button" 
              className="lb-sidebar-close-btn" 
              onClick={() => setIsMobileSidebarOpen(false)}
            >
              ✕
            </button>
          </div>

          {currentYearInfo.name && (
            <div className="lb-current-year-badge">
              ⭐ {currentYearInfo.name}
            </div>
          )}

          <ul className="lb-sidebar-group-list">
            <li className="lb-sidebar-group-item">
              <button
                onClick={() => handleSelectClass('all')}
                className={`lb-sidebar-btn all-classes-btn ${selectedClass === 'all' ? 'active' : ''}`}
              >
                🏫 Tất cả các lớp
              </button>
            </li>

            {sidebarLoading ? (
              <li className="lb-sidebar-msg">⏳ Đang tải danh sách lớp...</li>
            ) : Object.keys(dynamicBlocks).length === 0 ? (
              <li className="lb-sidebar-msg empty">Chưa có lớp nào trong niên khóa này.</li>
            ) : (
              Object.entries(dynamicBlocks).map(([groupKey, groupData]) => (
                <li key={groupKey} className="lb-sidebar-group-item">
                  <button 
                    onClick={() => toggleBlock(groupKey)} 
                    className="lb-sidebar-btn block-btn"
                  >
                    <span>{groupData.groupName}</span>
                    <span className={`lb-block-arrow ${openBlocks[groupKey] ? 'open' : ''}`}>▸</span>
                  </button>

                  {openBlocks[groupKey] && (
                    <ul className="lb-sidebar-submenu">
                      {groupData.classes.map((classItem) => (
                        <li key={classItem.id}>
                          <button
                            onClick={() => handleSelectClass(classItem.id)}
                            className={`lb-sidebar-btn class-btn ${selectedClass === classItem.id ? 'active' : ''}`}
                          >
                            {classItem.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))
            )}
          </ul>
        </aside>
      </>
    );
  };

  const showSidebar = location.pathname.startsWith('/liturgy-board/early-attendance');

  return (
    <div className="lb-page-container">
      <TopNav />
      {renderNavTabs()}
      
      <div className="lb-content-wrapper">
        {showSidebar && (
          <>
            <div className="lb-mobile-filter-bar">
              <button 
                type="button"
                className="lb-mobile-sidebar-toggle-btn"
                onClick={() => setIsMobileSidebarOpen(true)}
              >
                🏫 Chọn Lớp: <strong>{selectedClassNameDisplay}</strong> ▾
              </button>
            </div>
            {renderSidebar()}
          </>
        )}

        <main className="lb-main-content">
          <Outlet context={{ allStudentsForOutlet, selectedClassForOutlet: selectedClass }} />
        </main>
      </div>
    </div>
  );
};

export default LiturgyBoard;